"use client";

import type { DietAddonGroupDietAddonsRead } from "@repo/api/catalog/api/schemas/diet-addon-group-diet-addons-read";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

// Arrows are desktop-only: the mobile design scrolls the tabs by swipe and
// shows no chevron even when the categories overflow. They are unmounted when
// there is nothing left to scroll in that direction.
const ARROW_CLASS =
  "-translate-y-1/2 absolute top-1/2 z-10 flex items-center justify-center rounded-xl border border-brand-grey2-300 bg-brand-white p-3 text-brand-green1-300 hover:bg-brand-grey1-300 max-md:hidden";

// `inset-y-0` keeps the gradient exactly as tall as the tab row; a fixed height
// would stick out above and below it.
const FADE_CLASS =
  "pointer-events-none absolute inset-y-0 hidden w-26.5 from-transparent md:block";

// Shared so tabs rendered through `leadingTab` (the mobile "Twoje dodatki" tab)
// look identical to the category ones.
export const addonTabClassName = (isActive: boolean) =>
  cn(
    "shrink-0 whitespace-nowrap border-b-2 px-4 py-[11px] font-medium text-lg leading-[26px]",
    isActive
      ? "border-brand-green2-300 text-brand-green1-300"
      : "border-transparent text-brand-black-100 hover:text-brand-green1-300",
  );

type AddonCategoryTabsProps = {
  categories: Pick<DietAddonGroupDietAddonsRead, "slug" | "name">[];
  activeCategory: string;
  onSelectCategory: (slug: string) => void;
  // Rendered before the category tabs — used by the summary dialog on mobile for
  // its "Twoje dodatki" tab, which scrolls together with the categories.
  leadingTab?: React.ReactNode;
  // Colour the arrow's gradient fades blend into; defaults to the white surface
  // of the addons dialog. The configuration section sits on a grey card.
  fadeClassName?: string;
  className?: string;
};

// A sub-pixel gap is left over at the scroll extremes on fractional layouts;
// treat anything below this as "fully scrolled" so the arrow does not linger.
const SCROLL_EPSILON = 1;

// Category tab bar on a natively scrollable row, so the tabs never widen their
// container when there are many categories. A carousel would fight this view:
// tabs need to reach an exact left-edge position on click, which snap points
// (and their clamping at the row's end) cannot express. The chevron buttons
// only appear when the tabs actually overflow, matching the design ("Jeśli
// kategorie zaczynają być ukryte pokazujemy strzałkę").
export const AddonCategoryTabs = ({
  categories,
  activeCategory,
  onSelectCategory,
  leadingTab,
  fadeClassName = "to-brand-white",
  className,
}: AddonCategoryTabsProps) => {
  const t = useTranslations();
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const syncArrows = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) {
      return;
    }
    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    setCanScrollPrev(scroller.scrollLeft > SCROLL_EPSILON);
    setCanScrollNext(scroller.scrollLeft < maxScroll - SCROLL_EPSILON);
  }, []);

  // The row can start hidden (dialog mount) or reflow on resize, so its
  // scrollability is re-measured instead of read once.
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) {
      return;
    }

    syncArrows();
    const observer = new ResizeObserver(syncArrows);
    observer.observe(scroller);

    return () => observer.disconnect();
  }, [syncArrows]);

  // Clicking a tab pulls it to the left edge of the row, revealing the
  // categories that follow it — on mobile the row is scrolled by swipe only, so
  // a tab tapped near the right edge would otherwise hide the rest. The browser
  // clamps the target for the last tabs, which cannot reach the edge.
  const selectCategory = (slug: string, tab: HTMLButtonElement) => {
    onSelectCategory(slug);
    tab.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "start",
    });
  };

  // One arrow click moves a whole viewport of tabs.
  const scrollByViewport = (direction: 1 | -1) => {
    const scroller = scrollerRef.current;
    scroller?.scrollBy({
      behavior: "smooth",
      left: direction * scroller.clientWidth,
    });
  };

  return (
    <div className={cn("relative w-full min-w-0", className)}>
      {/* The underline sits behind the tabs and outside the scroller, so it
          spans the row at rest instead of scrolling away with the tabs. The
          active tab's own border paints over it — which only holds because the
          scroller is positioned too: an absolute element paints above static
          siblings, so without `relative` below the grey line covers the
          active border. */}
      <span
        aria-hidden="true"
        className="absolute inset-x-0 bottom-0 h-0.5 bg-brand-grey2-300"
      />

      {/* `no-scrollbar` keeps the swipe area clean on touch devices; the arrows
          and fades are the desktop affordance. */}
      <div
        className="no-scrollbar relative flex overflow-x-auto"
        onScroll={syncArrows}
        ref={scrollerRef}
      >
        {leadingTab}
        {categories.map((category) => (
          <button
            className={addonTabClassName(category.slug === activeCategory)}
            key={category.slug}
            onClick={(event) =>
              selectCategory(category.slug, event.currentTarget)
            }
            type="button"
          >
            {category.name}
          </button>
        ))}
      </div>

      {canScrollPrev && (
        <>
          <button
            aria-label={t("cart.configuration.addons.categories.previous")}
            className={cn(ARROW_CLASS, "left-0")}
            onClick={() => scrollByViewport(-1)}
            type="button"
          >
            <IconChevronLeft className="size-4" stroke={1.25} />
          </button>
          <span
            aria-hidden="true"
            className={cn(FADE_CLASS, "left-0 bg-gradient-to-l", fadeClassName)}
          />
        </>
      )}

      {canScrollNext && (
        <>
          <button
            aria-label={t("cart.configuration.addons.categories.next")}
            className={cn(ARROW_CLASS, "right-0")}
            onClick={() => scrollByViewport(1)}
            type="button"
          >
            <IconChevronRight className="size-4" stroke={1.25} />
          </button>
          <span
            aria-hidden="true"
            className={cn(
              FADE_CLASS,
              "right-0 bg-gradient-to-r",
              fadeClassName,
            )}
          />
        </>
      )}
    </div>
  );
};
